import { useStyle } from "./Audit.styles";
import { FC, useMemo, useState } from "react";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MantineReactTable,
  useMantineReactTable,
  MRT_ColumnFiltersState,
  MRT_SortingState,
} from "mantine-react-table";
import useGetAuditData from "../../hooks/useGetAuditData";
import { AuditData } from "../../services/getAuditData";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { Box, Button } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { downloadFile } from "@funded-here-interface/common/src/Utils/file";
import useDownloadAuditCSV from "../../hooks/useDownloadAuditCSV";
import { FileType } from "@funded-here-interface/common/src/constant/enum";
import { PERSONA_DISPLAY_MAP } from "@funded-here-interface/common/src/constant/constant";
import { toast } from "react-toastify";

const Audit: FC = () => {
  const { classes } = useStyle();

  const token = useSelector((state: RootState) => state.auth.token);

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  const useDownloadAuditCSVMutation = useDownloadAuditCSV();

  const apiColumnFilters: any = JSON.stringify(
    columnFilters
      .filter((filter) => {
        if (Array.isArray(filter.value)) {
          return filter.value.every(
            (v) => v !== null && v !== undefined && v !== ""
          );
        }
        return (
          filter.value !== null &&
          filter.value !== undefined &&
          filter.value !== ""
        );
      })
      .map((filter) => {
        let column = filter.id;
        let value = filter.value;

        return {
          [column]: value,
        };
      })
  );

  const apiSorting: any = JSON.stringify(
    sorting.reduce((acc, sort) => {
      if (sort.id && sort.desc !== undefined) {
        acc[sort.id] = sort.desc;
      }
      return acc;
    }, {} as Record<string, any>)
  );

  const { data, error, isLoading } = useGetAuditData(
    token,
    pagination,
    apiColumnFilters,
    apiSorting
  );

  if (error) {
    toast.error((error as Error).message);
  }

  const totalRowCount = data?.meta?.totalRowCount ?? 0;

  const handleDownloadAudit = () => {
    useDownloadAuditCSVMutation.mutate(
      { token, apiColumnFilters, apiSorting },
      {
        onSuccess: (data: Blob) => {
          downloadFile(data, "Audit", FileType.CSV);
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };
  const columns = useMemo<MRT_ColumnDef<AuditData>[]>(
    () => [
      {
        accessorKey: "userId",
        header: "User ID",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "name",
        header: "Name",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "email",
        header: "Email",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "role",
        header: "Role",
        filterFn: "equals",
        mantineFilterSelectProps: {
          data: [
            "investor",
            "merchant",
            "distributor",
            "product-manager",
            "sales-support",
            "ispv",
            "operation-and-risk",
            "merchant-spv",
            "funding-spv",
            "auditor",
          ].map((role) => ({
            value: role,
            label: PERSONA_DISPLAY_MAP[role] || role,
          })),
        },
        filterVariant: "select",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>
              {PERSONA_DISPLAY_MAP[renderedCellValue as string] ||
                renderedCellValue}
            </span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "action",
        header: "Action",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "method",
        header: "Method",
        filterFn: "equals",
        mantineFilterSelectProps: {
          data: ["GET", "POST", "PUT", "DELETE", "PATCH"] as string[],
        },
        filterVariant: "select",
      },
      {
        accessorKey: "description",
        header: "Description",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "url",
        header: "URL",
      },
      {
        accessorKey: "createdAtFormatted",
        header: "Triggered Date",
        filterVariant: "date-range",
      },
    ],
    []
  );

  const table = useMantineReactTable({
    enableGlobalFilter: false,
    enableColumnActions: true,
    enableColumnFilters: true,
    enableDensityToggle: false,
    enableMultiSort: true,
    paginationDisplayMode: "pages",
    state: { pagination, isLoading, sorting }, //pass the pagination and isLoading state to the table
    columns,
    data: data?.data ?? [],
    manualPagination: true,
    onPaginationChange: setPagination, //hoist pagination state to your state when it changes internally
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    rowCount: totalRowCount, //pass the total row count to the table
    initialState: { density: "xs" },
    mantineTableProps: {
      withColumnBorders: true,
      withBorder: true,
    },
    mantineTableHeadCellProps: {
      style: {
        padding: "0.5rem 1.0rem",
        color: "#23262a",
      },
    },
    mantineTableBodyCellProps: {
      style: {
        padding: "0.5rem",
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
        }}
      >
        <Button
          color="lightblue"
          //export all data that is currently in the table (ignore pagination, sorting, filtering, etc.)
          onClick={handleDownloadAudit}
          leftIcon={<IconDownload />}
          variant="filled"
        >
          Export Audit Log
        </Button>
      </Box>
    ),
  });

  return (
    <div>
      <div>
        <h1 className={classes.headerText}>Audit</h1>
      </div>

      <div>
        <MantineReactTable table={table} />
      </div>
    </div>
  );
};

export default Audit;
